import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import { TbReportSearch } from "react-icons/tb";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { User } from "@/@types/sintegrada/User";
import { getProfessionals } from "@/services/sintegrada/professional/getProfessionals";

import {
	ExamItemDashboardData,
	getExamItemDashboard,
} from "@/services/sintegrada/exam/getExamItemDashboard";

import { ExamDashboardFilters } from "./ExamDashboardFilters";

import { ExamStatisticsCards } from "./ExamStatisticsCards";
import { ExamTypeStatistics } from "./ExamTypeStatistics";
import { ProceduresList } from "./ProceduresList";
import { MedicationAdministration } from "./MedicationAdministration";
import { VitalSigns } from "./VitalSigns";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PrintComponent } from "@/components/PrintComponent";

dayjs.extend(utc);
dayjs.extend(timezone);

export function ExamDashboard() {
	const { uf, ibgeCode, cnes } = useCurrentAccount();
	const { setTitle } = useLayout();
	const [startDate, setStartDate] = useState(
		dayjs().add(-1, "month").startOf("day").format("YYYY-MM-DDTHH:mm")
	);
	const [endDate, setEndDate] = useState(dayjs().endOf("day").format("YYYY-MM-DDTHH:mm"));
	const [minAge, setMinAge] = useState<number>();
	const [maxAge, setMaxAge] = useState<number>();
	const [professionalId, setProfessionalId] = useState<number>();
	const [professionals, setProfessionals] = useState<User[]>([]);
	const [dashboard, setDashboard] = useState<ExamItemDashboardData>();
	const [isLoading, setIsLoading] = useState(false);
	const printPageRef = useRef<HTMLDivElement | null>(null);

	async function fetchDashboard() {
		setIsLoading(true);
		setDashboard(
			await getExamItemDashboard({
				uf,
				ibgeCode,
				cnes,
				startDate: dayjs(startDate)
					.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
					.startOf("minute")
					.format("YYYY-MM-DDTHH:mm:ssZ"),
				endDate: dayjs(endDate)
					.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
					.endOf("minute")
					.format("YYYY-MM-DDTHH:mm:ssZ"),
				minAge,
				maxAge,
				professionalId,
			})
		);
		setIsLoading(false);
	}

	async function fetchProfessional() {
		setProfessionals(await getProfessionals({ uf, ibgeCode, cnes }));
	}

	const printPage = useReactToPrint({
		content: () => printPageRef.current,
	});

	useEffect(() => {
		setTitle("Monitoramento - Exames e procedimentos");
		fetchDashboard();
		fetchProfessional();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="p-2">
			<LoadingScreen loading={isLoading} />
			<Row className="p-2">
				<Col sm={12} lg={12}>
					<Card className="h-100 shadow-sm">
						<Card.Body>
							<ExamDashboardFilters
								startDate={startDate}
								setStartDate={setStartDate}
								endDate={endDate}
								setEndDate={setEndDate}
								setMinAge={setMinAge}
								setMaxAge={setMaxAge}
								setProfessionalId={setProfessionalId}
								professionals={professionals}
								handleFilter={fetchDashboard}
								handlePrint={printPage}
							/>
						</Card.Body>
					</Card>
				</Col>
			</Row>
			<Row className="p-2">
				<Col>
					<Card className="bg-white shadow-sm">
						<Card.Header className="p-0">
							<Card.Title className="d-flex align-items-center gap-2 p-2 text-primary mb-0">
								<TbReportSearch
									size={30}
									className="bg-primary text-white rounded p-1 me-2"
								/>
								Exames e procedimentos da unidade
							</Card.Title>
						</Card.Header>
					</Card>
				</Col>
			</Row>
			<PrintComponent
				printComponentRef={printPageRef}
				headerContainer={
					<div>
						<p className="text-center fs-3">Exames e procedimentos da unidade</p>
						{startDate && endDate && (
							<div>
								<p className="text-center">
									<span>{`${dayjs(startDate).format("DD/MM/YYYY")} Á ${dayjs(
										endDate
									).format("DD/MM/YYYY")}`}</span>
								</p>
							</div>
						)}
						{minAge && maxAge && (
							<p>
								<strong>Faixa etária:</strong>
								<span>{`${minAge} anos á ${maxAge} anos`}</span>
							</p>
						)}
						{professionalId && (
							<p>
								<strong>Profissional:</strong>
								<span>
									{professionals.find((p) => p.id === professionalId)?.name}
								</span>
							</p>
						)}
					</div>
				}
			>
				{/* Cards de Estatísticas Detalhadas */}
				<ExamStatisticsCards
					totalExamsRequested={
						dashboard?.totalExamsRequested || { interno: 0, eletivo: 0 }
					}
					totalExamsCompleted={
						dashboard?.totalExamsCompleted || { interno: 0, eletivo: 0 }
					}
					totalExamsCancelled={
						dashboard?.totalExamsCancelled || { interno: 0, eletivo: 0 }
					}
				/>

				{/* Cards de Estatísticas por Tipo de Exame */}
				<ExamTypeStatistics
					totalLaboratoryExams={
						dashboard?.totalLaboratoryExams || {
							requested: 0,
							completed: 0,
							cancelled: 0,
						}
					}
					totalRadiographyExams={
						dashboard?.totalRadiographyExams || {
							requested: 0,
							completed: 0,
							cancelled: 0,
						}
					}
				/>

				{/* Lista de Procedimentos Realizados */}
				<ProceduresList
					totalProceduresByProcedure={dashboard?.totalProceduresByProcedure || []}
				/>

				{/* Administração de Medicamentos */}
				<Row className="p-2">
					<Col sm={12} lg={12} className="mb-3">
						<MedicationAdministration
							totalMedicationAdministration={
								dashboard?.totalMedicationAdministration || []
							}
						/>
					</Col>
				</Row>

				{/* Sinais Vitais */}
				<Row className="p-2">
					<Col sm={12} lg={12} className="mb-3">
						<VitalSigns
							totalVitalSigns={dashboard?.totalVitalSigns || []}
						/>
					</Col>
				</Row>
			</PrintComponent>
		</div>
	);
}
